import { Injectable, inject, signal } from '@angular/core';
import { FloatingPanelService } from '@shared/floating-panel/floating-panel.service';
import { HdGateInfoOpenBehavior } from '../bodygraph/gate-info/hd-gate-info-requested-context.model';
import { loadHdChartGlobalSettings } from '../bodygraph/chart-tabs/hd-chart-global-settings';
import { HdPlanetKey } from '../bodygraph/planet-grid/hd-planet-display-config';
import { HdFixedStarPanelComponent } from './hd-fixed-star-panel.component';
import { HdFixedStarsExtrasInfoComponent } from './hd-fixed-stars-extras-info.component';
import { HdFixedStarsGeneralInfoComponent } from './hd-fixed-stars-general-info.component';
import {
  HdFixedStarSkyChartPanelComponent,
  HdFixedStarSkyChartPanelData,
} from './hd-fixed-star-sky-chart-panel.component';
import { getHdFixedStarBySide } from './hd-fixed-stars.metadata';

/** Chart-context van de klik: welke activatie leverde deze ster op. */
export interface HdFixedStarPanelContext {
  readonly planetKey: HdPlanetKey;
  readonly activationSide: 'design' | 'personality';
  readonly activationLongitude: number;
  readonly correctionDegrees: number;
  readonly correctedLongitude: number;
  readonly distanceDegrees: number;
  /** Poort.lijn van de activatie in de chart. */
  readonly gate: number;
  readonly line: number;
}

export interface HdFixedStarPanelData {
  /** Side-nummer (1..66) van de ster. */
  readonly side: number;
  /** `null` wanneer het paneel zonder chart-context geopend wordt. */
  readonly context: HdFixedStarPanelContext | null;
}

export function buildHdFixedStarPanelKey(side: number): string {
  return `fixed-star:${side}`;
}

/**
 * Opent het fixed-star-info-paneel voor één van de 66 Sides. Volgt hetzelfde
 * dock-/dialoogpatroon als {@link HdFearsPanelService}: de keuze komt uit de
 * globale `gateInfoOpenBehavior`-setting. Eén paneel per ster; heropenen met
 * een andere context vervangt de paneel-data.
 */
@Injectable({ providedIn: 'root' })
export class HdFixedStarsPanelService {
  private readonly floatingPanels = inject(FloatingPanelService);

  private readonly openBehavior = signal<HdGateInfoOpenBehavior>(
    loadHdChartGlobalSettings().gateInfoOpenBehavior,
  );

  /** Live-sync van de `gateInfoOpenBehavior`-setting door de chart-pagina's. */
  setOpenBehavior(behavior: HdGateInfoOpenBehavior): void {
    this.openBehavior.set(behavior);
  }

  openForStar(
    side: number,
    context: HdFixedStarPanelContext | null,
    origin?: { x: number; y: number },
  ): void {
    const star = getHdFixedStarBySide(side);
    if (!star) {
      return;
    }
    const behavior = this.openBehavior();
    const docked = behavior === 'full-docked-keep' || behavior === 'full-docked-autoclose';
    const data: HdFixedStarPanelData = { side, context };

    const ref = this.floatingPanels.openOrFocus<HdFixedStarPanelComponent, HdFixedStarPanelData>({
      key: buildHdFixedStarPanelKey(side),
      component: HdFixedStarPanelComponent,
      data,
      dockTab: { label: star.name, icon: 'star' },
      dockZone: 'full',
      openInitiallyDocked: docked,
      redockOnReopen: docked,
      startPosition: origin != null
        ? { preferred: origin, hint: 'prefer-right-of-anchor' }
        : 'center',
    });
    // Verse referentie zodat een al open paneel de nieuwe context toont.
    ref.replaceData({ ...data });
  }

  /** Opent de algemene info-pagina over fixed stars, de 66 Sides en de berekening. */
  openGeneralInfo(origin?: { x: number; y: number }): void {
    const behavior = this.openBehavior();
    const docked = behavior === 'full-docked-keep' || behavior === 'full-docked-autoclose';
    this.floatingPanels.openOrFocus<HdFixedStarsGeneralInfoComponent, null>({
      key: 'fixed-stars:general-info',
      component: HdFixedStarsGeneralInfoComponent,
      data: null,
      dockTab: { label: 'Fixed stars', icon: 'star' },
      dockZone: 'full',
      openInitiallyDocked: docked,
      redockOnReopen: docked,
      startPosition: origin != null
        ? { preferred: origin, hint: 'prefer-right-of-anchor' }
        : 'center',
    });
  }

  /**
   * Opent de vergrote sterrenkaart als grote, zwevende dialog. Bewust altijd
   * meteen zwevend en niet gedockt (ongeacht de gateInfoOpenBehavior-setting):
   * de kaart is een eyecatcher die groot getoond moet worden. `bypassAutoDock`
   * omdat de grote kaart bijna de viewport vult en de overlap-heuristiek anders
   * zou docken.
   */
  openSkyChart(side: number): void {
    const star = getHdFixedStarBySide(side);
    if (!star) {
      return;
    }
    this.floatingPanels.openOrFocus<HdFixedStarSkyChartPanelComponent, HdFixedStarSkyChartPanelData>({
      key: `fixed-star-sky:${side}`,
      component: HdFixedStarSkyChartPanelComponent,
      data: { side },
      dockTab: { label: star.name, icon: 'star' },
      dockZone: 'full',
      openInitiallyDocked: false,
      bypassAutoDock: true,
      redockOnReopen: false,
      startPosition: 'center',
    });
  }

  /** Opent het uitleg-paneel bij de "Extra fixed stars"-lijst. */
  openExtrasInfo(origin?: { x: number; y: number }): void {
    const behavior = this.openBehavior();
    const docked = behavior === 'full-docked-keep' || behavior === 'full-docked-autoclose';
    this.floatingPanels.openOrFocus<HdFixedStarsExtrasInfoComponent, null>({
      key: 'fixed-stars:extras-info',
      component: HdFixedStarsExtrasInfoComponent,
      data: null,
      dockTab: { label: 'Extra fixed stars', icon: 'star' },
      dockZone: 'full',
      openInitiallyDocked: docked,
      redockOnReopen: docked,
      startPosition: origin != null
        ? { preferred: origin, hint: 'prefer-right-of-anchor' }
        : 'center',
    });
  }
}
